import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { CircleXIcon, RotateCwIcon } from '@air/design-icons';
import { FileStatusButton } from '@air/feature-file-status/ui';
import { setUploadsResumedAction, uploadByIdSelector } from '@air/redux-uploader';
import { useDeleteUpload } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import { usePrivateUploadDelete } from '~/components/Upload/hooks/usePrivateUploadDelete';
import { WorkspaceUploadsListItem } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsListItem';
import { useAirSelector } from '~/utils/ReduxUtils';

export type WorkspaceUploadsFailedListItemProps = {
  uploadId: string;
};

export const WorkspaceUploadsFailedListItem = memo(({ uploadId }: WorkspaceUploadsFailedListItemProps) => {
  const dispatch = useDispatch();
  const upload = useAirSelector((state) => uploadByIdSelector(state, uploadId));
  const { onDeleteUpload } = usePrivateUploadDelete({ analyticsContext: 'workspace_uploads' });
  const { deleteUpload } = useDeleteUpload({ onDelete: onDeleteUpload });

  const handleRetry = useCallback(() => {
    dispatch(setUploadsResumedAction({ uploadIds: [uploadId] }));
  }, [dispatch, uploadId]);

  const handleCancel = useCallback(() => {
    if (!upload) {
      return;
    }

    deleteUpload(upload).catch((error: unknown) => {
      reportErrorToBugsnag({ error, context: 'Failed to cancel upload' });
    });
  }, [deleteUpload, upload]);

  if (!upload) {
    return null;
  }

  return (
    <WorkspaceUploadsListItem
      actions={
        <>
          <FileStatusButton
            appearance="ghost"
            data-testid="WORKSPACE_UPLOADS_ITEM_RETRY"
            label="Retry"
            onClick={handleRetry}
          >
            <RotateCwIcon aria-hidden size="sm" />
          </FileStatusButton>
          <FileStatusButton
            appearance="ghost"
            data-testid="WORKSPACE_UPLOADS_ITEM_CANCEL"
            label="Cancel"
            onClick={handleCancel}
          >
            <CircleXIcon aria-hidden size="sm" />
          </FileStatusButton>
        </>
      }
      ext={upload.apiUploadData.ext ?? ''}
      statusCopy={`Error: ${upload.errorMessage ?? 'Failed'}`}
      thumbnailSource={upload.imageBase64}
      title={upload.apiUploadData.importedName ?? ''}
      uploadId={upload.id}
    />
  );
});

WorkspaceUploadsFailedListItem.displayName = 'WorkspaceUploadsFailedListItem';
